import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import {
  AuditLogApiService,
  AuditLogQuery,
  AuditLogRow,
  AuditVerifyResult,
} from 'src/app/core/services/audit-log-api.service';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { AuditEntryDrawerComponent } from './audit-entry-drawer.component';
import { AuditLogTableComponent } from './audit-log-table.component';
import { endOfDay, startOfDay } from './audit-diff.util';

/** Filters that live outside the shared toolbar (search / result / date range). */
interface ExtraFilters {
  resource_type: string;
  source: string;
  tenant_id: number | null;
  source_ip: string;
}
const EMPTY_EXTRA: ExtraFilters = { resource_type: '', source: '', tenant_id: null, source_ip: '' };

@Component({
  selector: 'app-audit-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    AuditLogTableComponent,
    AuditEntryDrawerComponent,
  ],
  templateUrl: './audit-log.component.html',
})
export class AuditLogComponent implements OnInit {
  private readonly api = inject(AuditLogApiService);

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: { placeholder: 'Search by email, action or record…' },
      status: {
        label: 'Result',
        options: [
          { value: 'success', label: 'Done' },
          { value: 'failure', label: 'Failed' },
        ],
      },
      dateRange: { placeholder: 'Date range' },
    },
    pagination: true,
  };

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
  extra: ExtraFilters = { ...EMPTY_EXTRA };
  moreOpen = false;

  rows: AuditLogRow[] = [];
  meta: ListModulePaginationMeta | null = null;
  page = 1;
  readonly pageSize = 25;
  loading = false;
  error = '';

  verify: AuditVerifyResult | null = null;
  verifying = false;

  selected: AuditLogRow | null = null;
  exporting = false;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .list({ page: this.page, page_size: this.pageSize, ...this.queryFromFilters() })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.rows = res?.data?.result ?? [];
          const total = res?.data?.total ?? 0;
          this.meta = {
            total,
            currentPage: this.page,
            perPage: this.pageSize,
            lastPage: Math.max(1, Math.ceil(total / this.pageSize)),
          };
        },
        error: () => {
          this.error = 'The audit log could not be loaded. Try again in a moment.';
          this.meta = null;
        },
      });
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.page = 1;
    this.load();
  }

  onListPageChange(page: number): void {
    this.page = page;
    this.load();
  }

  applyExtra(): void {
    this.page = 1;
    this.load();
  }

  clearExtra(): void {
    this.extra = { ...EMPTY_EXTRA };
    this.applyExtra();
  }

  get extraCount(): number {
    const e = this.extra;
    return [e.resource_type, e.source, e.source_ip].filter(Boolean).length + (e.tenant_id ? 1 : 0);
  }

  runVerify(): void {
    this.verifying = true;
    this.api
      .verify()
      .pipe(finalize(() => (this.verifying = false)))
      .subscribe({
        next: (res) => (this.verify = res?.data ?? null),
        error: () => (this.verify = null),
      });
  }

  /** Same filter set feeds list() and exportCsv() so the CSV matches what is on screen. */
  exportCsv(): void {
    this.exporting = true;
    this.api
      .exportCsv(this.queryFromFilters())
      .pipe(finalize(() => (this.exporting = false)))
      .subscribe({
        next: (blob) => this.downloadBlob(blob, 'audit-log.csv'),
        error: () => (this.error = 'The CSV could not be exported. Try again in a moment.'),
      });
  }

  private queryFromFilters(): AuditLogQuery {
    const f = this.listFilters;
    const e = this.extra;
    return {
      search: f.searchQuery.trim() || undefined,
      outcome: f.status || undefined,
      // Both bounds sent as local wall-clock so the range is inclusive of both days.
      date_from: f.dateRangeStart ? startOfDay(f.dateRangeStart) : undefined,
      date_to: f.dateRangeEnd ? endOfDay(f.dateRangeEnd) : undefined,
      resource_type: e.resource_type || undefined,
      source: e.source || undefined,
      tenant_id: e.tenant_id ?? undefined,
      source_ip: e.source_ip || undefined,
    };
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}
